import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MapContainer from '../components/map/MapContainer';
import MapSidebar from '../components/map/MapSidebar';
import PastaForm from '../components/forms/PastaForm';
import OpportunityDetails from '../components/map/OpportunityDetails';
import ScoutPanel from '../components/analysis/ScoutPanel';
import AnalysisResults from '../components/analysis/AnalysisResults';
import AIMarketAnalysis from '../components/analysis/AIMarketAnalysis';
import MapContextMenu from '../components/map/MapContextMenu';
import { useAuth } from '../store/AuthContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import { Eye, EyeOff } from 'lucide-react';

// Etiqueta de marca para agrupar/mostrar: preserva acrónimos en mayúsculas
// (BK, KFC) y pasa el resto a Title Case ("burger king" → "Burger king").
const brandLabel = (b) => {
  const s = String(b || '').trim() || 'Unbranded';
  return s === s.toUpperCase() ? s : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const Dashboard = ({ activeFilter, scoutActive, refreshKey, hiddenBrands, onToggleBrandVisibility, searchQuery, searchTrigger }) => {
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [scoutResults, setScoutResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapBounds, setMapBounds] = useState(null);
  const [intelStats, setIntelStats] = useState([]);

  // Scanner States
  const [scanRadius, setScanRadius] = useState(1000);
  const [maxScanRadius, setMaxScanRadius] = useState(2000);
  const [scanResults, setScanResults] = useState(null);
  const [savedScanId, setSavedScanId] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [viableMarkers, setViableMarkers] = useState([]);
  const [nearestItems, setNearestItems] = useState(null);
  const [coordinatePoint, setCoordinatePoint] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { position: {x,y}, coords: {lat,lng} }

  // Debounce bounds updates to avoid re-rendering the entire panel on every map move
  const boundsTimerRef = useRef(null);
  const handleBoundsChanged = useCallback((bounds) => {
    if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
    boundsTimerRef.current = setTimeout(() => setMapBounds(bounds), 300);
  }, []);

  useEffect(() => {
    const fetchScoutDefaults = async () => {
      try {
        const { data } = await axios.get(`${getApiUrl()}/setup/scout-params`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.data?.scout_scan_radius) {
          setMaxScanRadius(Number(data.data.scout_scan_radius));
        }
      } catch {
        // use defaults
      }
    };
    if (token) fetchScoutDefaults();
  }, [token]);

  useEffect(() => {
    fetchOpportunities();
  }, [token, refreshKey]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${getApiUrl()}/intel/sources`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIntelStats(data.sources || []);
      } catch {
        // non-critical
      }
    };
    fetchStats();
  }, [token, refreshKey]);

  useEffect(() => {
    setScanResults(null); // Reset scan results when opportunity changes
  }, [selectedOpportunity]);

  // Calculate distances to nearest base, engine and competence
  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const computeNearest = useCallback((selLat, selLng, excludeId) => {
    const findNearest = (type) => {
      let nearest = null;
      let minDist = Infinity;
      for (const opp of opportunities) {
        if (opp._id === excludeId) continue;
        if ((opp.type || 'base') !== type) continue;
        const c = opp.location?.coordinates;
        if (!c) continue;
        const dist = haversine(selLat, selLng, c[1], c[0]);
        if (dist < minDist) { minDist = dist; nearest = { name: opp.name, distance: Math.round(dist) }; }
      }
      return nearest;
    };
    return {
      base: findNearest('base'),
      engine: findNearest('engine'),
      competence: findNearest('competence')
    };
  }, [opportunities]);

  useEffect(() => {
    if (!selectedOpportunity || !opportunities.length) {
      setNearestItems(null);
      return;
    }
    const coords = selectedOpportunity.location?.coordinates;
    if (!coords) {
      setNearestItems(null);
      return;
    }
    const [selLng, selLat] = coords;
    setNearestItems(computeNearest(selLat, selLng, selectedOpportunity._id));
  }, [selectedOpportunity, opportunities, computeNearest]);

  useEffect(() => {
    let filtered = opportunities;
    
    // Group and count brands
    const brandCounts = opportunities.reduce((acc, o) => {
      const bName = brandLabel(o.brand);
      const key = `${bName}|${o.type || 'base'}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    // Multi-brand visibility filter (excluding hidden ones)
    if (hiddenBrands && hiddenBrands.size > 0) {
      filtered = filtered.filter(o => {
        const bName = brandLabel(o.brand);
        return !hiddenBrands.has(`${bName}|${o.type || 'base'}`);
      });
    }
    
    // Specific active filter
    if (activeFilter) {
      filtered = filtered.filter(o => {
        const bName = brandLabel(o.brand);
        const targetName = brandLabel(activeFilter.brand);
        return bName === targetName && (o.type || 'base') === activeFilter.type;
      });
    }
    
    // Map with counts
    const withCounts = filtered.map(o => {
      const bName = brandLabel(o.brand);
      return {
        ...o,
        nameWithCount: `${o.name} (${brandCounts[`${bName}|${o.type || 'base'}`] || 0})`
      };
    });

    setFilteredOpportunities(withCounts);
  }, [activeFilter, opportunities, hiddenBrands]); // Removed searchQuery from here

  const fetchOpportunities = async () => {
    try {
      const resp = await axios.get(`${getApiUrl()}/base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpportunities(resp.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDetails = useCallback(() => {
    setSelectedOpportunity(null);
    setCoordinatePoint(null);
  }, []);

  const handleScanClick = useCallback(async () => {
    if (!selectedOpportunity) return;
    
    setIsScanning(true);
    try {
      if (!selectedOpportunity.location || !selectedOpportunity.location.coordinates) {
        alert('Mission error: No geographic coordinates for this site.');
        return;
      }

      const isCoordPoint = selectedOpportunity._isCoordinatePoint;
      const resp = await axios.post(`${getApiUrl()}/scout/scan`, {
        ...(isCoordPoint ? {} : {
          referenceId: selectedOpportunity._id,
          referenceType: selectedOpportunity.type || 'base',
        }),
        lat: selectedOpportunity.location.coordinates[1],
        lng: selectedOpportunity.location.coordinates[0],
        radius: scanRadius
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanResults(resp.data.results);
      setSavedScanId(resp.data.savedScanId);
    } catch (err) {
      alert('Scan error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsScanning(false);
    }
  }, [selectedOpportunity, scanRadius, token]);

  const handleMapClick = useCallback((location) => {
    setSelectedLocation(location);
    setSelectedOpportunity(null);
    setCoordinatePoint(null);
    setShowForm(false);
  }, []);

  const handleMapDblClick = useCallback((location) => {
    // Show context menu at click pixel position
    setContextMenu({
      position: { x: location.x || 0, y: location.y || 0 },
      coords: { lat: location.lat, lng: location.lng },
    });
    // Also set coordinate point marker
    const synth = {
      _id: `COORD-${location.lat.toFixed(6)}-${location.lng.toFixed(6)}`,
      _isCoordinatePoint: true,
      name: `${location.lat.toFixed(5)}°, ${location.lng.toFixed(5)}°`,
      type: 'coordinate',
      source: 'map-point',
      address: `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
      location: { coordinates: [location.lng, location.lat] },
      ingestedAt: new Date().toISOString(),
    };
    setSelectedOpportunity(synth);
    setCoordinatePoint(location);
    setSelectedLocation(null);
    setShowForm(false);
    setScanResults(null);
    setNearestItems(computeNearest(location.lat, location.lng, null));
  }, [computeNearest]);

  const handleMarkerClick = useCallback((opp) => {
    setSelectedOpportunity(opp);
    setSelectedLocation(null);
    setCoordinatePoint(null);
    setShowForm(false);
    setViableMarkers([]);
  }, []);

  const handleScoutClick = useCallback((scout) => {
    setSelectedLocation({
      lat: scout.lat,
      lng: scout.lng,
      name: scout.name
    });
    setSelectedOpportunity(null);
    setShowForm(false);
  }, []);

  const handleFormSubmit = async (data) => {
    const { file, ...opportunityData } = data;
    try {
      // 1. Create the opportunity
      const resp = await axios.post(`${getApiUrl()}/opportunities`, opportunityData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let newOpp = resp.data;

      // 2. If there's a file, upload it to the newly created ID
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResp = await axios.post(
          `${getApiUrl()}/opportunities/${newOpp._id}/attachments`,
          formData,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        newOpp = uploadResp.data;
      }

      setOpportunities([...opportunities, newOpp]);
      setShowForm(false);
      setSelectedLocation(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving opportunity');
    }
  };

  const scanCircle = useMemo(() => ({
    center: (selectedOpportunity?.location?.coordinates) ? {
      lat: selectedOpportunity.location.coordinates[1],
      lng: selectedOpportunity.location.coordinates[0]
    } : null,
    radius: scanRadius,
    visible: !!selectedOpportunity
  }), [selectedOpportunity, scanRadius]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: '#05070A', color: '#0B3D91' }}>
      <div className="nasa-loader">INITIALIZING SPATIAL ANALYTICS...</div>
    </div>
  );

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'row', height: '100%', position: 'relative', overflow: 'hidden' }}>

      {/* Brand filter chips floating over the map */}
      {(() => {
        // Derive unique brands with counts and colors from unfiltered opportunities
        const brandMap = {};
        opportunities.forEach((o) => {
          const bName = brandLabel(o.brand);
          const key = `${bName}|${o.type || 'base'}`;
          if (!brandMap[key]) {
            brandMap[key] = { brand: bName, type: o.type || 'base', count: 0, color: o.visuals?.color || '#636e72' };
          }
          brandMap[key].count++;
        });
        const brandList = Object.values(brandMap);
        if (brandList.length === 0) return null;

        return (
          <div
            style={{
              position: 'absolute',
              top: '14px',
              left: '14px',
              zIndex: 60,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              maxWidth: isMobile ? 'calc(100% - 16px)' : 'calc(100% - 270px)',
              maxHeight: isMobile ? '84px' : 'none',
              overflowY: isMobile ? 'auto' : 'visible',
              pointerEvents: 'all',
            }}
          >
            {brandList.map(({ brand, type, count, color }) => {
              const key = `${brand}|${type}`;
              const hidden = hiddenBrands && hiddenBrands.has(key);
              return (
                <button
                  key={key}
                  onClick={() => onToggleBrandVisibility && onToggleBrandVisibility(brand, type)}
                  title={hidden ? `Mostrar ${brand}` : `Ocultar ${brand}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 10px',
                    borderRadius: '14px',
                    border: `1px solid ${hidden ? '#2d3436' : color}`,
                    backgroundColor: hidden ? 'rgba(10,15,26,0.85)' : `${color}22`,
                    color: hidden ? '#636e72' : 'white',
                    cursor: 'pointer',
                    fontSize: '10px',
                    fontFamily: "'Orbitron', sans-serif",
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: hidden ? '#2d3436' : color,
                      flexShrink: 0,
                    }}
                  />
                  {brand}
                  <span style={{ opacity: 0.7, fontSize: '9px' }}>{count}</span>
                  {hidden ? <EyeOff size={10} /> : <Eye size={10} />}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Intel sources badge (top-right of map) */}
      {!isMobile && intelStats.length > 0 && (() => {
        const loaded = intelStats.filter((s) => s.status === 'loaded');
        const total = intelStats.reduce((sum, s) => sum + s.count, 0);
        if (total === 0) return null;
        return (
          <div
            style={{
              position: 'absolute',
              top: '14px',
              right: '254px',
              zIndex: 60,
              backgroundColor: 'rgba(5,7,10,0.88)',
              border: '1px solid #1a1f2e',
              borderRadius: '6px',
              padding: '5px 10px',
              fontSize: '9px',
              letterSpacing: '1px',
              color: '#636e72',
              fontFamily: "'Orbitron', sans-serif",
              backdropFilter: 'blur(4px)',
            }}
          >
            <span style={{ color: '#00CEC9' }}>{loaded.length}</span> FUENTES /{' '}
            <span style={{ color: '#00CEC9' }}>{total.toLocaleString()}</span> LOCALES
          </div>
        );
      })()}

      <MapContainer 
        onMapClick={handleMapClick}
        onMapDblClick={handleMapDblClick}
        onMarkerClick={handleMarkerClick}
        onScoutClick={handleScoutClick}
        opportunities={filteredOpportunities} 
        scoutResults={scoutResults}
        viableMarkers={viableMarkers}
        onBoundsChanged={handleBoundsChanged}
        coordinatePoint={coordinatePoint}
        scanCircle={scanCircle}
        addressToSearch={searchQuery}
        searchTrigger={searchTrigger}
      />

      {/* Sidebar with visible markers — displaces the map */}
      <MapSidebar
        opportunities={filteredOpportunities}
        mapBounds={mapBounds}
        onItemClick={handleMarkerClick}
      />
      
      {/* Overlay - Pasta Form */}
      {showForm && selectedLocation && (
        <div style={{ position: 'absolute', top: isMobile ? '70px' : '20px', left: isMobile ? '8px' : '20px', zIndex: 100 }}>
          <PastaForm 
            location={selectedLocation} 
            initialName={selectedLocation.name || ''}
            onSubmit={handleFormSubmit}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Overlay - Opportunity Details */}
      {selectedOpportunity && (
        <div style={{ position: 'absolute', top: isMobile ? '70px' : '20px', left: isMobile ? '8px' : '20px', zIndex: 100 }}>
          <OpportunityDetails 
            opportunity={selectedOpportunity} 
            onClose={handleCloseDetails} 
            scanRadius={scanRadius}
            setScanRadius={setScanRadius}
            maxScanRadius={maxScanRadius}
            onScan={handleScanClick}
            scanResults={scanResults}
            savedScanId={savedScanId}
            isScanning={isScanning}
            nearestItems={nearestItems}
          />
        </div>
      )}

      {/* Floating Mission Data Panels */}
      <div style={{
        position: 'absolute',
        top: isMobile ? '70px' : '20px',
        right: isMobile ? '8px' : '275px',
        left: isMobile ? '8px' : 'auto',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        maxHeight: isMobile ? 'calc(100dvh - 200px)' : 'calc(100dvh - 100px)',
        overflowY: 'auto'
      }}>
        {/* Overpass Scout Panel */}
        {scoutActive && (
          <ScoutPanel onResultsFound={(results) => setScoutResults(results)} />
        )}

        {/* AI Market Analysis Panel */}
        {scoutActive && (
          <AIMarketAnalysis token={token} />
        )}

        {/* Huff Analysis Panel */}
        {scoutActive && (
          <AnalysisResults mapBounds={mapBounds} onViableFound={setViableMarkers} />
        )}

        {/* Mission Inventory Panel */}
        {scoutActive && (
          <div style={{ 
            background: 'rgba(5, 7, 10, 0.9)', 
            border: '1px solid #0B3D91',
            padding: '15px',
            borderRadius: '4px',
            color: '#F0F0F0',
            width: '280px',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ color: '#E27B58', fontSize: '0.85rem', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Mission Inventory
            </h3>
            
            <div style={{ fontSize: '0.8rem', marginBottom: '8px', color: '#AAA' }}>
              MY SITES: <span style={{ color: '#FFF' }}>{opportunities.length}</span><br/>
              SCOUTED: <span style={{ color: '#0B3D91' }}>{scoutResults.length}</span>
            </div>

            <div style={{ borderTop: '1px solid #1A1A1A', marginTop: '10px', paddingTop: '10px' }}>
              {selectedLocation ? (
                <>
                  <p style={{ fontSize: '0.7rem', color: '#888', marginBottom: '5px' }}>
                    {selectedLocation.name ? `SCOUT TARGET: ${selectedLocation.name}` : 'TARGET LOCKED:'}
                  </p>
                  <div style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                    LAT: {selectedLocation.lat.toFixed(5)}<br/>
                    LNG: {selectedLocation.lng.toFixed(5)}
                  </div>
                  {!showForm && (
                    <button 
                      className="nasa-button" 
                      style={{ 
                        width: '100%', 
                        marginTop: '12px', 
                        fontSize: '0.75rem',
                        borderColor: selectedLocation.name ? '#E27B58' : '#0B3D91'
                      }}
                      onClick={() => setShowForm(true)}
                    >
                      {selectedLocation.name ? 'CONVERT TO OPPORTUNITY' : 'DEPLOY PASTA PROBE'}
                    </button>
                  )}
                </>
              ) : (
                <p style={{ fontSize: '0.75rem', color: '#555', fontStyle: 'italic' }}>
                  Select a location on the map to begin site evaluation...
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Map context menu on double-click */}
    {contextMenu && (
      <MapContextMenu
        position={contextMenu.position}
        coords={contextMenu.coords}
        onClose={() => setContextMenu(null)}
      />
    )}
    </>
  );
};

export default Dashboard;
